import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A tiny "we're building your report" placeholder shown in the popup window
// while the backend query is in flight. Keeping this synchronous with the
// user's click is CRITICAL: browsers will silently block window.open() if
// it happens after an awaited async call — that's the "have to click twice"
// symptom users see when the API is slow.
const LOADING_HTML = `<!DOCTYPE html><html><head><title>Preparing report…</title><style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #334155; display: flex; align-items: center; justify-content: center; min-height: 100vh; margin: 0; background: #f8fafc; }
  .card { text-align: center; padding: 32px 40px; border: 1px solid #e2e8f0; border-radius: 8px; background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.04); max-width: 380px; }
  .spinner { width: 32px; height: 32px; border: 3px solid #e2e8f0; border-top-color: #f59e0b; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 16px; }
  h1 { font-size: 15px; margin: 0 0 6px; font-weight: 600; }
  p { font-size: 12px; color: #64748b; margin: 0; line-height: 1.5; }
  @keyframes spin { to { transform: rotate(360deg); } }
</style></head><body>
  <div class="card">
    <div class="spinner"></div>
    <h1>Preparing your report…</h1>
    <p>Gathering donations, recipients, and yearly totals from the database.<br>This can take up to a minute for donors with thousands of records.</p>
  </div>
</body></html>`;

export async function generatePrintReport({ summary, sortBy, sortOrder, displayTimeRange }) {
  const isDonor = summary.type === 'donor';

  // 1) Open the popup IMMEDIATELY in the same synchronous tick as the user's
  //    click so browsers treat it as user-initiated (never blocked, never
  //    requires a second click). Then we fetch data in parallel.
  const w = window.open('', '_blank');
  if (w) {
    w.document.open();
    w.document.write(LOADING_HTML);
    w.document.close();
  }

  const reportUrl = isDonor
    ? `${API}/report/donor/${encodeURIComponent(summary.name)}?sort_by=${sortBy}&sort_order=${sortOrder}`
    : `${API}/report/recipient/${encodeURIComponent(summary.name)}?sort_by=${sortBy}&sort_order=${sortOrder}`;
  let report;
  try {
    const reportRes = await axios.get(reportUrl, { timeout: 90000 });
    report = reportRes.data;
  } catch (err) {
    if (w) {
      w.document.open();
      w.document.write(`<!DOCTYPE html><html><body style="font-family:sans-serif;padding:32px;text-align:center;">
        <h2 style="color:#dc2626;">Could not build report</h2>
        <p style="color:#64748b;">${err.message || 'The server did not respond in time.'} Please close this tab and try again in a moment.</p>
      </body></html>`);
      w.document.close();
    }
    throw err;
  }

  const allRelated = isDonor ? report.all_recipients : report.all_donors;
  const allDonations = report.all_donations || [];

  const yearData = (isDonor ? (summary.by_year_party || summary.by_year) : summary.by_year)
    ?.filter(d => d.year >= displayTimeRange.start && d.year <= displayTimeRange.end) || [];
  const usesParty = isDonor && summary.by_year_party && summary.by_year_party.length > 0;

  const fmtCur = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v);
  const fmtShort = (v) => { if (v >= 1000000) return `$${(v/1000000).toFixed(1)}M`; if (v >= 1000) return `$${(v/1000).toFixed(1)}K`; return `$${v.toFixed(0)}`; };

  const relatedRows = allRelated?.map(r =>
    `<tr><td style="padding:5px 10px;border-bottom:1px solid #e2e8f0;">${r.name}</td><td style="padding:5px 10px;border-bottom:1px solid #e2e8f0;text-align:right;font-family:monospace;">${fmtCur(r.total)}</td><td style="padding:5px 10px;border-bottom:1px solid #e2e8f0;text-align:right;font-family:monospace;">${r.count.toLocaleString()}</td></tr>`
  ).join('') || '';

  const yearRows = yearData?.map(r => {
    if (usesParty) {
      return `<tr><td style="padding:5px 10px;border-bottom:1px solid #e2e8f0;">${r.year}</td><td style="padding:5px 10px;border-bottom:1px solid #e2e8f0;text-align:right;font-family:monospace;color:#2563eb;">${r.democrat ? fmtCur(r.democrat) : '-'}</td><td style="padding:5px 10px;border-bottom:1px solid #e2e8f0;text-align:right;font-family:monospace;color:#dc2626;">${r.republican ? fmtCur(r.republican) : '-'}</td><td style="padding:5px 10px;border-bottom:1px solid #e2e8f0;text-align:right;font-family:monospace;">${r.other ? fmtCur(r.other) : '-'}</td><td style="padding:5px 10px;border-bottom:1px solid #e2e8f0;text-align:right;font-family:monospace;font-weight:600;">${fmtCur(r.total)}</td><td style="padding:5px 10px;border-bottom:1px solid #e2e8f0;text-align:right;font-family:monospace;">${r.count.toLocaleString()}</td></tr>`;
    }
    return `<tr><td style="padding:5px 10px;border-bottom:1px solid #e2e8f0;">${r.year}</td><td style="padding:5px 10px;border-bottom:1px solid #e2e8f0;text-align:right;font-family:monospace;font-weight:600;">${fmtCur(r.total)}</td><td style="padding:5px 10px;border-bottom:1px solid #e2e8f0;text-align:right;font-family:monospace;">${r.count.toLocaleString()}</td></tr>`;
  }).join('') || '';

  const yearHeader = usesParty
    ? '<th style="padding:6px 10px;text-align:left;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;">Year</th><th style="padding:6px 10px;text-align:right;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;color:#2563eb;">Democrat</th><th style="padding:6px 10px;text-align:right;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;color:#dc2626;">Republican</th><th style="padding:6px 10px;text-align:right;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;">Other</th><th style="padding:6px 10px;text-align:right;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;">Total</th><th style="padding:6px 10px;text-align:right;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;"># Donations</th>'
    : '<th style="padding:6px 10px;text-align:left;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;">Year</th><th style="padding:6px 10px;text-align:right;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;">Total</th><th style="padding:6px 10px;text-align:right;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;"># Donations</th>';

  const donationRows = allDonations.map(d =>
    `<tr><td class="dt-year">${d.report_year || ''}</td><td class="dt-donor">${d.contributor_name || ''}</td><td class="dt-recip">${d.committee_name || ''}</td><td class="dt-amt">${fmtCur(d.contribution_amount)}</td><td class="dt-date">${d.contribution_date || ''}</td></tr>`
  ).join('');

  const generatedDate = new Date().toLocaleString();

  const html = `<!DOCTYPE html><html><head><title>${isDonor ? 'Donor' : 'Recipient'} Report - ${summary.name}</title><style>
    @page {
      size: letter;
      margin: 0.5in 0.4in 0.8in 0.4in;
      @bottom-left { content: "Generated ${generatedDate}"; font-size: 8px; color: #94a3b8; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
      @bottom-center { content: "${summary.name} — FEC ${isDonor ? 'Donor' : 'Recipient'} Report"; font-size: 8px; color: #94a3b8; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
      @bottom-right { content: "Page " counter(page) " of " counter(pages); font-size: 8px; color: #64748b; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
    }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1e293b; max-width: 1000px; margin: 0 auto; padding: 20px; font-size: 12px; }
    h1 { font-size: 22px; margin: 0 0 4px; } h2 { font-size: 14px; margin: 20px 0 6px; color: #334155; border-bottom: 2px solid #e2e8f0; padding-bottom: 4px; }
    .meta { color: #64748b; font-size: 12px; margin-bottom: 16px; } .meta span { margin-right: 14px; }
    .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 14px 0; }
    .stat-card { border: 1px solid #e2e8f0; border-radius: 4px; padding: 10px; } .stat-label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; margin-bottom: 3px; }
    .stat-value { font-size: 18px; font-weight: 600; font-family: monospace; } .stat-value.green { color: #059669; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; } th { background: #f8fafc; font-size: 10px; }
    .donations-table { table-layout: fixed; font-size: 9px; }
    .donations-table td, .donations-table th { padding: 2px 5px; border-bottom: 1px solid #f1f5f9; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .donations-table th { border-bottom: 2px solid #cbd5e1; font-size: 8px; text-transform: uppercase; background: #f8fafc; }
    .dt-year { width: 7%; font-family: monospace; }
    .dt-donor { width: 25%; }
    .dt-recip { width: 33%; }
    .dt-amt { width: 15%; text-align: right; font-family: monospace; }
    .dt-date { width: 20%; font-family: monospace; }
    .page-break { page-break-before: always; }
    @media screen { .screen-footer { color: #94a3b8; font-size: 11px; text-align: center; margin-top: 32px; padding-top: 12px; border-top: 1px solid #e2e8f0; } }
    @media print { .screen-footer { display: none; } }
  </style></head><body>
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:6px;">
      <div><h1>${summary.name}</h1><div class="meta">
        <span>${summary.year_range}</span>
        ${summary.address ? `<span>${summary.address}</span>` : ''}
        ${summary.employer ? `<span>Employer: ${summary.employer}</span>` : ''}
        ${summary.occupation ? `<span>Occupation: ${summary.occupation}</span>` : ''}
      </div></div>
      <div style="text-align:right;font-size:10px;color:#94a3b8;">FEC ${isDonor ? 'Donor' : 'Recipient'} Report</div>
    </div>

    <div class="stats">
      <div class="stat-card"><div class="stat-label">Total Donated</div><div class="stat-value green">${fmtShort(summary.total_amount)}</div></div>
      <div class="stat-card"><div class="stat-label"># Donations</div><div class="stat-value">${summary.donation_count?.toLocaleString()}</div></div>
      <div class="stat-card"><div class="stat-label">Average</div><div class="stat-value">${fmtCur(summary.avg_donation || 0)}</div></div>
      <div class="stat-card"><div class="stat-label">${isDonor ? 'Recipients' : 'Unique Donors'}</div><div class="stat-value">${isDonor ? allRelated?.length || 0 : summary.unique_donor_count || 0}</div></div>
    </div>

    <h2>Donations by Year</h2>
    <table><thead><tr>${yearHeader}</tr></thead><tbody>${yearRows}</tbody></table>

    <h2>All ${isDonor ? 'Recipients' : 'Donors'} (${allRelated?.length || 0})</h2>
    <table><thead><tr>
      <th style="padding:6px 10px;text-align:left;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;">Name</th>
      <th style="padding:6px 10px;text-align:right;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;">Total</th>
      <th style="padding:6px 10px;text-align:right;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;"># Donations</th>
    </tr></thead><tbody>${relatedRows}</tbody></table>

    <h2 class="page-break">All Donation Records (${allDonations.length.toLocaleString()})</h2>
    <table class="donations-table"><thead><tr>
      <th class="dt-year">Year</th>
      <th class="dt-donor">Donor</th>
      <th class="dt-recip">Recipient</th>
      <th class="dt-amt">Amount</th>
      <th class="dt-date">Date</th>
    </tr></thead><tbody>${donationRows}</tbody></table>

    <div class="screen-footer">Generated ${generatedDate} &mdash; Page numbers appear in print preview</div>
  </body></html>`;

  if (w) {
    // Replace the loading placeholder with the finished report.
    w.document.open();
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 600);
  }
}
